import React, { useState, useEffect, memo } from 'react';
import moment from 'moment';
import { FileAttachment } from './fileAttachment';
import { DeleteMessageModal } from './deleteMessageModal';
import { CustomDelete } from '../../components';
import { chatActions } from '../../store';
import { useModal } from '../../utils';
import { useDispatch } from 'react-redux';

const DELETE_MESSAGE_ACTION_TYPE = 'DELETE_MESSAGE';
export const MessageBubble = memo(({ message, isSent, isGroup, onDelete, currentUserId, currentUserType }) => {
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [showCopyToast, setShowCopyToast] = useState(false);
    const { open } = useModal();
    const dispatch = useDispatch();
    const isRead = isSent && (message?.readBy || []).some((r) => r.userId !== currentUserId);

    const senderName = message?.sender?.user ? `${message.sender.user.firstName} ${message.sender.user.lastName}` : '';

    const senderAvatar = message?.sender?.user?.avatar || null;
    const senderInitials = senderName
        ? senderName
              .split(' ')
              .map((n) => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2)
        : '?';

    const handleCopy = () => {
        if (message?.text) {
            navigator.clipboard.writeText(message.text).then(() => {
                setShowCopyToast(true);
            });
        }
    };

    useEffect(() => {
        if (showCopyToast) {
            const timer = setTimeout(() => setShowCopyToast(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [showCopyToast]);

    const handleDeleteConfirm = () => {
        if (onDelete) {
            onDelete(message.id);
        }
        setDeleteModalOpen(false);
    };

    const timestamp = moment(message?.createdAt).format('dddd h:mm A');

    return (
        <>
            <div className={`chat-message-row ${isSent ? 'chat-message-row-sent' : 'chat-message-row-received'}`}>
                {/* Avatar column for received messages — Figma style */}
                {!isSent && (
                    <div className="chat-message-avatar-col">
                        {senderAvatar ? (
                            <img src={senderAvatar} alt={senderName} className="chat-avatar" />
                        ) : (
                            <div className="chat-avatar">{senderInitials}</div>
                        )}
                    </div>
                )}

                <div className="chat-message-bubble-wrapper">
                    {/* Sender name + timestamp ABOVE the bubble — Figma style */}
                    <div className={`chat-message-meta ${isSent ? 'chat-message-meta-sent' : ''}`}>
                        {!isSent && <span className="chat-message-sender-name">{isSent ? 'You' : senderName}</span>}
                        <span className="chat-message-meta-time">{timestamp}</span>
                        {isSent && (
                            <span className="chat-read-receipt">
                                {isRead ? (
                                    <svg width="16" height="10" viewBox="0 0 16 10" fill="none" title="Read">
                                        <path
                                            d="M1 5.5l3 3L10 2"
                                            stroke="#347AF0"
                                            strokeWidth="1.4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M6 5.5l3 3L15 2"
                                            stroke="#347AF0"
                                            strokeWidth="1.4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                ) : (
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" title="Sent">
                                        <path
                                            d="M1 5.5l3 3L9 2"
                                            stroke="#98A2B3"
                                            strokeWidth="1.4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                )}
                            </span>
                        )}
                    </div>

                    {message?.text && (
                        <div className={`chat-message-bubble ${isSent ? 'chat-sent-bubble' : 'chat-received-bubble'}`}>
                            <div>{message.text}</div>
                        </div>
                    )}
                    {message?.attachments?.map((att, idx) => (
                        <FileAttachment key={att.fileId || idx} attachment={att} isSent={isSent} />
                    ))}

                    <div className={`chat-message-actions ${isSent ? 'chat-message-actions-sent' : 'chat-message-actions-received'}`}>
                        <button className="chat-message-action-btn" onClick={handleCopy} title="Copy">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path
                                    d="M4.1665 12.5003H3.33317C2.89114 12.5003 2.46722 12.3247 2.15466 12.0122C1.8421 11.6996 1.6665 11.2757 1.6665 10.8337V3.33366C1.6665 2.89163 1.8421 2.46771 2.15466 2.15515C2.46722 1.84259 2.89114 1.66699 3.33317 1.66699H10.8332C11.2752 1.66699 11.6991 1.84259 12.0117 2.15515C12.3242 2.46771 12.4998 2.89163 12.4998 3.33366V4.16699M9.1665 7.50033H16.6665C17.587 7.50033 18.3332 8.24652 18.3332 9.16699V16.667C18.3332 17.5875 17.587 18.3337 16.6665 18.3337H9.1665C8.24603 18.3337 7.49984 17.5875 7.49984 16.667V9.16699C7.49984 8.24652 8.24603 7.50033 9.1665 7.50033Z"
                                    stroke="#85888E"
                                    stroke-width="1.67"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </button>
                        {isSent && (
                            <button
                                className="chat-message-action-btn"
                                onClick={() =>
                                    open(
                                        <CustomDelete
                                            info="Delete message ?"
                                            text="  Are you sure you want to delete this message? This action cannot be undone."
                                            handleDel={() => dispatch(chatActions.deleteMessage(message.id))}
                                            actionType={DELETE_MESSAGE_ACTION_TYPE}
                                        />
                                    )
                                }
                                title="Delete"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path
                                        d="M2.5 5.00033H4.16667M4.16667 5.00033H17.5M4.16667 5.00033V16.667C4.16667 17.109 4.34226 17.5329 4.65482 17.8455C4.96738 18.1581 5.39131 18.3337 5.83333 18.3337H14.1667C14.6087 18.3337 15.0326 18.1581 15.3452 17.8455C15.6577 17.5329 15.8333 17.109 15.8333 16.667V5.00033H4.16667ZM6.66667 5.00033V3.33366C6.66667 2.89163 6.84226 2.46771 7.15482 2.15515C7.46738 1.84259 7.89131 1.66699 8.33333 1.66699H11.6667C12.1087 1.66699 12.5326 1.84259 12.8452 2.15515C13.1577 2.46771 13.3333 2.89163 13.3333 3.33366V5.00033M8.33333 9.16699V14.167M11.6667 9.16699V14.167"
                                        stroke="white"
                                        stroke-width="1.67"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {showCopyToast && (
                <div className="chat-copy-toast">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="10" fill="#12B76A" />
                        <path d="M14 7l-5.5 5.5L6 10" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Message copied!
                    <button className="chat-toast-close-btn" onClick={() => setShowCopyToast(false)} aria-label="Close">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M10.5 3.5l-7 7M3.5 3.5l7 7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>
            )}

            <DeleteMessageModal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} onConfirm={handleDeleteConfirm} />
        </>
    );
});
